import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { adminApi, type BatchEnrollmentStats } from '@/lib/api/admin';
import { toast } from 'sonner';
import { formatTimestamp } from '@/lib/date';

export const Route = createFileRoute('/admin/batches/$batchId/enrollment')({
  component: EnrollmentRoute,
});

function stageBadge(pct: number) {
  if (pct === 0) return { cls: 'bg-gray-100 text-gray-700', label: 'Not Started' };
  if (pct <= 25) return { cls: 'bg-blue-100 text-blue-700', label: 'Just Started' };
  if (pct <= 75) return { cls: 'bg-yellow-100 text-yellow-700', label: 'In Progress' };
  if (pct < 100) return { cls: 'bg-orange-100 text-orange-700', label: 'Advanced' };
  return { cls: 'bg-green-100 text-green-700', label: 'Completed' };
}

function progressBarColor(pct: number) {
  if (pct === 0) return 'bg-gray-300';
  if (pct <= 25) return 'bg-blue-500';
  if (pct <= 75) return 'bg-yellow-500';
  if (pct < 100) return 'bg-orange-500';
  return 'bg-green-500';
}


interface SummaryCardProps {
  label: string;
  count: number;
  cls: string;
}

function SummaryCard({ label, count, cls }: SummaryCardProps) {
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <p className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</p>
      <p className="mt-1 text-3xl font-bold tabular-nums">{count}</p>
    </div>
  );
}

function EnrollmentRoute() {
  const { batchId } = Route.useParams();
  const [data, setData] = useState<BatchEnrollmentStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi
      .getBatchStudents(batchId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: any) => {
        if (!cancelled) toast.error(err?.response?.data?.error || 'Failed to load batch enrollment');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [batchId]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-muted-foreground">Batch not found.</p>;
  }

  return (
    <div className="space-y-6 p-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <SummaryCard label="Not Started" count={data.summary.not_started} cls="bg-gray-50 border-gray-200 text-gray-700" />
        <SummaryCard label="Just Started" count={data.summary.just_started} cls="bg-blue-50 border-blue-200 text-blue-700" />
        <SummaryCard label="In Progress" count={data.summary.in_progress} cls="bg-yellow-50 border-yellow-200 text-yellow-700" />
        <SummaryCard label="Advanced" count={data.summary.advanced} cls="bg-orange-50 border-orange-200 text-orange-700" />
        <SummaryCard label="Completed" count={data.summary.completed} cls="bg-green-50 border-green-200 text-green-700" />
      </div>

      {/* Students table */}
      <div className="overflow-hidden rounded-lg border border-border bg-background shadow-sm">
        <div className="border-b border-border px-5 py-3.5">
          <h2 className="text-[13px] font-semibold text-foreground">Students</h2>
        </div>
        {data.students.length === 0 ? (
          <p className="px-5 py-6 text-[13px] text-muted-foreground">No students enrolled.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {['Student', 'Progress', 'Topics Done', 'Stage', 'Enrollment Status', 'Enrolled'].map((h) => (
                  <th key={h} className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {data.students.map((s) => {
                const badge = stageBadge(s.completion_pct);
                return (
                  <tr key={s.user_id} className="hover:bg-muted/30">
                    <td className="px-5 py-3">
                      <div className="flex flex-col">
                        <span className="text-[13px] font-medium text-foreground">{s.username}</span>
                        <span className="text-[12px] text-muted-foreground">{s.email}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
                          <div
                            className={`h-full rounded-full ${progressBarColor(s.completion_pct)}`}
                            style={{ width: `${s.completion_pct}%` }}
                          />
                        </div>
                        <span className="text-[12px] tabular-nums text-muted-foreground">
                          {Math.round(s.completion_pct)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-[13px] tabular-nums text-muted-foreground">
                      {s.completed_topics}/{s.total_topics}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[12px] capitalize text-muted-foreground">
                      {s.enrollment_status.replace(/_/g, ' ')}
                    </td>
                    <td className="px-5 py-3 text-[12px] text-muted-foreground">
                      {formatTimestamp(s.enrolled_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
